import { isNull } from "lodash";
import { uniqBy } from "lodash";
import { useInfiniteQuery } from "react-query";
import { useInfiniteQueryHelpers } from "@/react/hooks/reactQuery/useInfiniteQueryHelpers";
import type { ChatThreadListItem } from "@/react/types/Chat/ChatRoomMessage";
import { usePunditUserContext } from "@circle-react/contexts";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useChatThreadListReaction } from "./useChatThreadListReaction";

const DEFAULT_PER_PAGE = 10;

export const queryKey = ["chat-threads-list"];

export const useChatThreadsList = () => {
  const { currentCommunityMember } = usePunditUserContext();
  const { updateAllRecords, findAndReplaceRecord } = useInfiniteQueryHelpers();
  const { handleReactionFromWebsocket } = useChatThreadListReaction();

  const updateQueryCache = (newData: any) => {
    updateAllRecords(newData, queryKey, DEFAULT_PER_PAGE);
  };

  const findAndReplaceThread = (thread: any) => {
    findAndReplaceRecord(thread, queryKey);
  };

  const updateChatThreads = (threadMessages: ChatThreadListItem[]) => {
    const parentMessageId = threadMessages[0].parent_message_id;

    const updatedChatThreads = chatThreads.map((thread: ChatThreadListItem) => {
      if (thread.parent_message.id === parentMessageId) {
        return {
          ...thread,
          replies: uniqBy([...threadMessages, ...thread.replies], "id"),
        };
      }
      return thread;
    });
    updateQueryCache(updatedChatThreads);
  };

  const fetchChatThreads = (page: number) =>
    reactQueryGet(
      internalApi.chatThreads.index({
        params: {
          page: page,
          per_page: DEFAULT_PER_PAGE,
        },
      }),
    );

  const {
    data,
    isLoading,
    refetch: refetchChatThreads,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => fetchChatThreads(pageParam),
    {
      select: data => ({
        pages: data.pages,
        pageParams: data.pageParams,
      }),
      enabled: !!currentCommunityMember?.policies?.can_access_threads_tab,
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    },
  );

  const chatThreads = data?.pages.flatMap(page => page.records) || [];

  const fetchChatThread = async (id: number) => {
    try {
      const data = await reactQueryGet(
        internalApi.chatThreads.show({
          id,
        }),
      );
      updateQueryCache([data, ...chatThreads]);
    } catch (err) {
      console.error(err);
    }
  };

  const onEventReceive = (eventData: any) => {
    switch (eventData.event) {
      case "newMessage":
        onNewMessage(eventData.json_message);
        break;
      case "updatedMessage":
        onUpdateMessage(eventData.json_message);
        break;
      case "deletedMessage":
        onDeleteMessage(eventData);
        break;
    }
  };

  const onReactionEventReceive = (eventData: any) => {
    handleReactionFromWebsocket(eventData, chatThreads);
  };

  const onNewMessage = (newMessage: ChatThreadListItem) => {
    if (
      newMessage?.unread_flow ||
      newMessage?.chat_room_parent_type === "Live::Room"
    ) {
      return;
    }
    const isNewThread = chatThreads.every(
      (thread: ChatThreadListItem) =>
        thread.parent_message.id !== newMessage.parent_message_id,
    );

    if (isNewThread) {
      void fetchChatThread(newMessage.chat_thread_id);
    } else {
      const updatedThread: ChatThreadListItem | undefined = chatThreads.find(
        (thread: ChatThreadListItem) =>
          thread.parent_message.id === newMessage.parent_message_id,
      );

      if (updatedThread) {
        const { parent_message, replies } = updatedThread;
        const result = {
          ...updatedThread,
          parent_message: {
            ...parent_message,
            replies_count: parent_message.replies_count + 1,
          },
          replies: [...replies, newMessage],
        };
        findAndReplaceThread(result);
      }
    }
  };

  const updateParentMessage = (message: ChatThreadListItem) => {
    const updatedThread: ChatThreadListItem | undefined = chatThreads.find(
      (thread: ChatThreadListItem) => thread.parent_message.id === message.id,
    );

    if (updatedThread) {
      const { parent_message } = updatedThread;
      const result = {
        ...updatedThread,
        parent_message: {
          ...parent_message,
          ...message,
        },
      };
      findAndReplaceThread(result);
    }
  };

  const onUpdateMessage = (message: ChatThreadListItem) => {
    if (isNull(message.parent_message_id)) {
      updateParentMessage(message);
    } else {
      const updatedThread: ChatThreadListItem | undefined = chatThreads.find(
        (thread: ChatThreadListItem) =>
          thread.parent_message.id === message.parent_message_id,
      );

      if (updatedThread) {
        const { replies } = updatedThread;
        const updatedReplies = replies.map(reply => {
          if (reply.id === message.id) return message;
          return reply;
        });
        const result = {
          ...updatedThread,
          replies: updatedReplies,
        };
        findAndReplaceThread(result);
      }
    }
  };

  const onDeleteMessage = (eventData: any) => {
    const { id: deletedMessageId, parent_message } = eventData || {};
    const { id: parentMessageId, replies_count: repliesCount } =
      parent_message || {};

    const isDeletedThread = chatThreads.some(
      (thread: ChatThreadListItem) =>
        thread.parent_message.id === deletedMessageId,
    );

    if (isDeletedThread) {
      updateQueryCache(
        chatThreads.filter(
          (thread: ChatThreadListItem) =>
            thread.parent_message.id !== deletedMessageId,
        ),
      );
    } else if (repliesCount === 0) {
      updateQueryCache(
        chatThreads.filter(
          (thread: ChatThreadListItem) =>
            thread.parent_message.id !== parentMessageId,
        ),
      );
    } else {
      const updatedChatThreads = chatThreads.map(
        (thread: ChatThreadListItem) => {
          if (thread.parent_message.id !== parentMessageId) return thread;

          const { replies } = thread;
          const updatedReplies = replies.filter(
            reply => reply.id !== deletedMessageId,
          );
          return {
            ...thread,
            parent_message,
            replies: updatedReplies,
          };
        },
      );
      updateQueryCache(updatedChatThreads);
    }
  };

  const isChatThreadsAvailable = chatThreads.length > 0;

  return {
    chatThreads,
    setChatThreads: updateQueryCache,
    isLoading,
    refetchChatThreads,
    isChatThreadsAvailable,
    hasNextPage,
    fetchNextPage,
    onEventReceive,
    updateChatThreads,
    onReactionEventReceive,
    findAndReplaceThread,
  };
};
